import React, { useMemo, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { toast } from "sonner";
import { X, Send, ChevronDown, Wand2 } from "lucide-react";
import { CLASSIFICATION_VALUES, CLASSIFICATION_LABELS } from "../lib/classification";
import { splitLineageText } from "../lib/lineageParse";
import ImageUploader from "./ImageUploader";
import {
  GENETIC_ENTRY_TYPES, PARENT_DRIVEN_ENTRY_TYPES, ORIGIN_DRIVEN_ENTRY_TYPES,
  CLONE_DRIVEN_ENTRY_TYPES, PARENT_ROLES, PARENT_TYPES, GENERATIONS,
  SELECTION_DIRECTIONS, POPULATION_TYPES, PRESERVATION_STATUSES,
  ENVIRONMENT_ADAPTATIONS, CUT_AUTHENTICITY, YES_NO_UNKNOWN, LINEAGE_CONFIDENCE,
  EVIDENCE_TYPES, DISPUTE_STATUSES, GENETIC_MARKER_TYPES, CHEMOTYPES,
  LEAF_TYPES, PHOTOPERIOD_TYPES, SEX_EXPRESSIONS, RESIN_PRODUCTION,
  EVIDENCE_FILE_CATEGORIES,
} from "../lib/lineageFormOptions";

/**
 * LineageSubmitModal — CannaForge Lineage Atlas submission form (v3.74).
 *
 * Architecture:
 *  - Submission TYPE (top) controls the broad lane (new_record vs corrections).
 *  - For `new_record` the form opens up into 10 progressive sections (A–J).
 *  - Conditional rendering keyed off `genetic_entry_type` keeps the form
 *    tight: parent block / landrace block / clone block / etc. only render
 *    when actually relevant.
 *  - Advanced sections (genetic testing, morphology, legal/credit, evidence)
 *    are collapsible to keep the casual contributor flow short.
 *
 * Backend contract: the payload travels through /api/lineage/submit as
 * `{ type, target_lineage_id, payload, note }`. The admin approval path
 * forwards every key on `payload` that maps to the LineageRecordIn shape.
 */

const SUB_TYPES = [
  { id: "new_record",   label: "Brand new strain",        hint: "Add a strain not in the atlas yet" },
  { id: "parentage",    label: "Parentage correction",    hint: "Fix parents or add lineage notes" },
  { id: "alias",        label: "Alias / alternate name",  hint: "Same strain known by another name" },
  { id: "originator",   label: "Originator info",         hint: "Who bred / preserved this" },
  { id: "source_notes", label: "Source notes",            hint: "Add context, history, references" },
  { id: "image",        label: "Image / photo",           hint: "Submit a grow photo for the gallery" },
  { id: "dispute",      label: "Dispute this lineage",    hint: "Flag the record for review" },
];

const EMPTY_PARENT = { name: "", role: "", type: "", source_breeder: "" };
const EMPTY_ORIGIN = {
  region: "", locality: "", collection_location: "", collection_year: "",
  collector: "", accession_id: "", population_type: "", preservation_status: "",
  open_pollinated_population_size: "", environmental_adaptation: [],
};
const EMPTY_CLONE = {
  cut_name: "", original_holder: "", suspected_lineage: "",
  cut_authenticity_status: "", reversal_used: "", selfed_line_exists: "",
  reversal_notes: "",
};
const EMPTY_VERIFICATION = {
  lineage_confidence: "", evidence_types: [], dispute_status: "no_dispute",
};
const EMPTY_GENETIC_TESTING = {
  test_available: "", lab: "", sample_id: "", marker_type: "",
  cluster_group: "", closest_matches: "", ancestry_composition: "",
};
const EMPTY_CHEMOTYPE = {
  chemotype: "", thc_range: "", cbd_range: "",
  dominant_cannabinoids: "", dominant_terpenes: "", aroma_flavor: "",
};
const EMPTY_MORPHOLOGY = {
  leaf_type: "", growth_structure: "", flowering_time: "",
  photoperiod_type: "", sex_expression: "", resin_production: "",
  bud_structure: "", yield_notes: "", resistance_notes: "",
};
const EMPTY_LEGAL = {
  submitted_by: "", claimed_by_breeder: "", original_breeder_credit: "",
  preservation_credit: "", commercial_release_date: "",
  ip_status: "", license_restrictions: "", do_not_rename: false,
};

const onlyTouched = (obj) => {
  // Strip empty strings / empty arrays so the backend gets a tidy payload.
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === "" || v === null || v === undefined) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
};

export default function LineageSubmitModal({ onClose, onSubmitted, targetLineageId = null, targetName = "", prefillStrainName = "" }) {
  const initialType = targetLineageId ? "parentage" : "new_record";
  const [type, setType] = useState(initialType);
  const [note, setNote] = useState("");

  // Basic identity
  const [strainName, setStrainName] = useState(prefillStrainName || "");
  const [aliasInput, setAliasInput] = useState("");
  const [originatorText, setOriginatorText] = useState("");
  const [classification, setClassification] = useState("");
  const [seedType, setSeedType] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [lineageNotes, setLineageNotes] = useState("");
  const [sourceNotes, setSourceNotes] = useState("");

  // Genetic entry type + the structured blocks
  const [geneticEntryType, setGeneticEntryType] = useState("");
  const [parentOne, setParentOne] = useState({ ...EMPTY_PARENT });
  const [parentTwo, setParentTwo] = useState({ ...EMPTY_PARENT });
  const [generation, setGeneration] = useState("");
  const [selectionDirection, setSelectionDirection] = useState("");
  const [plantsSelectedFrom, setPlantsSelectedFrom] = useState("");
  const [landraceOrigin, setLandraceOrigin] = useState({ ...EMPTY_ORIGIN });
  const [cloneDetails, setCloneDetails] = useState({ ...EMPTY_CLONE });
  const [verification, setVerification] = useState({ ...EMPTY_VERIFICATION });
  const [geneticTesting, setGeneticTesting] = useState({ ...EMPTY_GENETIC_TESTING });
  const [chemotype, setChemotype] = useState({ ...EMPTY_CHEMOTYPE });
  const [morphology, setMorphology] = useState({ ...EMPTY_MORPHOLOGY });
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [legalCredit, setLegalCredit] = useState({ ...EMPTY_LEGAL });
  const [images, setImages] = useState([]);

  const [busy, setBusy] = useState(false);

  // Conditional flags for the structured sections
  const showParentBlock = useMemo(
    () => PARENT_DRIVEN_ENTRY_TYPES.has(geneticEntryType),
    [geneticEntryType],
  );
  const showOriginBlock = useMemo(
    () => ORIGIN_DRIVEN_ENTRY_TYPES.has(geneticEntryType),
    [geneticEntryType],
  );
  const showCloneBlock = useMemo(
    () => CLONE_DRIVEN_ENTRY_TYPES.has(geneticEntryType),
    [geneticEntryType],
  );

  const buildPayload = () => {
    const p = {};
    if (type === "new_record") {
      p.strain_name = strainName.trim();
      if (aliasInput) p.aliases = aliasInput.split(",").map(a => a.trim()).filter(Boolean);
      if (originatorText) p.originator_text = originatorText.trim();
      if (classification) p.classification = classification;
      if (seedType) p.seed_type = seedType;
      if (tagsInput) p.tags = tagsInput.split(",").map(t => t.trim()).filter(Boolean);
      if (lineageNotes) p.lineage_notes = lineageNotes.trim();
      if (sourceNotes) p.source_notes = sourceNotes.trim();
      if (geneticEntryType) p.genetic_entry_type = geneticEntryType;

      // Parent block — also mirror primary names into parent_one / parent_two
      // so legacy consumers (graph, search) keep working.
      if (showParentBlock) {
        const p1 = onlyTouched(parentOne);
        const p2 = onlyTouched(parentTwo);
        if (p1) p.parent_one_detail = p1;
        if (p2) p.parent_two_detail = p2;
        if (p1?.name) p.parent_one = p1.name;
        if (p2?.name) p.parent_two = p2.name;
        if (generation) p.generation = generation;
        if (selectionDirection) p.selection_direction = selectionDirection;
        if (plantsSelectedFrom !== "") p.plants_selected_from = Number(plantsSelectedFrom);
      }

      if (showOriginBlock) {
        const origin = onlyTouched(landraceOrigin);
        if (origin) p.landrace_origin = origin;
        // legacy flag — mark as landrace/heirloom/preservation accordingly
        if (geneticEntryType === "landrace") p.landrace = true;
        if (geneticEntryType === "heirloom") p.heirloom = true;
        if (geneticEntryType === "preservation_accession") p.preservation = true;
      }

      if (showCloneBlock) {
        const clone = onlyTouched(cloneDetails);
        if (clone) p.clone_details = clone;
      }

      const v = onlyTouched(verification);
      if (v) p.verification = v;
      const gt = onlyTouched(geneticTesting);
      if (gt) p.genetic_testing = gt;
      const ch = onlyTouched(chemotype);
      if (ch) p.chemotype = ch;
      const mo = onlyTouched(morphology);
      if (mo) p.morphology = mo;
      if (evidenceFiles.length) p.evidence_files = evidenceFiles;
      const lc = onlyTouched(legalCredit);
      if (lc) p.legal_credit = lc;
      if (images.length) p.images = images;
    } else if (type === "parentage") {
      if (showParentBlock || parentOne.name || parentTwo.name) {
        const p1 = onlyTouched(parentOne);
        const p2 = onlyTouched(parentTwo);
        if (p1) { p.parent_one_detail = p1; if (p1.name) p.parent_one = p1.name; }
        if (p2) { p.parent_two_detail = p2; if (p2.name) p.parent_two = p2.name; }
      }
      if (lineageNotes) p.lineage_notes = lineageNotes.trim();
    } else if (type === "alias") {
      p.aliases = aliasInput.split(",").map(a => a.trim()).filter(Boolean);
    } else if (type === "originator") {
      p.originator_text = originatorText.trim();
    } else if (type === "source_notes") {
      p.source_notes = sourceNotes.trim();
    } else if (type === "image") {
      p.images = images;
    }
    // Dispute carries no structured payload — admin reads `note`.
    return p;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!targetLineageId && type === "new_record" && !strainName.trim()) {
      toast.error("Strain name is required for new records"); return;
    }
    if (!targetLineageId && type === "new_record" && !geneticEntryType) {
      toast.error("Pick a Genetic Entry Type so reviewers know how to read this submission"); return;
    }
    if (type === "image" && !images.length) {
      toast.error("Upload at least one image to submit."); return;
    }
    setBusy(true);
    try {
      const payload = buildPayload();
      await api.post("/lineage/submit", {
        type, target_lineage_id: targetLineageId, payload, note,
      });
      toast.success("Submission received — admin will review shortly.");
      onSubmitted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-stretch justify-end" data-testid="lineage-submit-modal">
      <div className="absolute inset-0" onClick={onClose}/>
      <div className="relative ml-auto w-full max-w-xl h-full overflow-y-auto bg-[var(--surface)] border-l border-[var(--border)]">
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-[var(--border)] bg-[var(--surface)]">
          <div>
            <div className="label-eyebrow" style={{color:"var(--gold)"}}>Lineage Atlas · Contribute</div>
            <div className="font-serif text-xl mt-1" style={{color:"var(--text)"}}>
              {targetName ? `Suggest a correction to "${targetName}"` : "Submit lineage info"}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--surface-2)]" data-testid="lineage-modal-close"><X className="w-4 h-4"/></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5">
          {/* ───── A. SUBMISSION TYPE ─────────────────────────────────── */}
          <SectionHeader letter="A" title="Submission type"/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SUB_TYPES
              .filter(t => targetLineageId ? t.id !== "new_record" : t.id !== "dispute")
              .map(t => (
                <button key={t.id} type="button" onClick={() => setType(t.id)}
                        className={`text-left px-3 py-2 border ${type === t.id ? "border-[var(--gold)] bg-[var(--surface-2)]" : "border-[var(--border)]"}`}
                        data-testid={`lineage-sub-type-${t.id}`}>
                  <div className="text-sm" style={{color: type === t.id ? "var(--gold)" : "var(--text)"}}>{t.label}</div>
                  <div className="text-[11px] mt-0.5" style={{color:"var(--text-muted)"}}>{t.hint}</div>
                </button>
              ))}
          </div>

          {type === "new_record" && (
            <>
              {/* ───── B. BASIC IDENTITY ─────────────────────────────── */}
              <SectionHeader letter="B" title="Basic identity"/>
              <Field label="Strain name *">
                <input className="input-field w-full" value={strainName} onChange={e=>setStrainName(e.target.value)}
                       maxLength={160} data-testid="lineage-field-strain_name"/>
              </Field>
              <Field label="Aliases (comma-separated)">
                <input className="input-field w-full" value={aliasInput} onChange={e=>setAliasInput(e.target.value)} data-testid="lineage-field-aliases"/>
              </Field>
              <Field label="Originator / breeder (text)">
                <input className="input-field w-full" value={originatorText} onChange={e=>setOriginatorText(e.target.value)} maxLength={160} data-testid="lineage-field-originator"/>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Classification">
                  <select className="input-field w-full" value={classification} onChange={e=>setClassification(e.target.value)} data-testid="lineage-field-classification">
                    <option value="">— Unknown / Not specified —</option>
                    {CLASSIFICATION_VALUES.filter(v => v !== "unknown").map(v => (
                      <option key={v} value={v}>{CLASSIFICATION_LABELS[v]}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Seed type">
                  <select className="input-field w-full" value={seedType} onChange={e=>setSeedType(e.target.value)} data-testid="lineage-field-seed-type">
                    <option value="">—</option>
                    <option value="regular">Regular</option>
                    <option value="feminized">Feminized</option>
                    <option value="autoflower">Autoflower</option>
                    <option value="clone_only">Clone only</option>
                  </select>
                </Field>
              </div>
              <Field label="Tags (comma-separated)">
                <input className="input-field w-full" value={tagsInput} onChange={e=>setTagsInput(e.target.value)} placeholder="fuel, fruity, gas…" data-testid="lineage-field-tags"/>
              </Field>

              {/* ───── C. GENETIC ENTRY TYPE ─────────────────────────── */}
              <SectionHeader letter="C" title="Genetic entry type"/>
              <Field label="Genetic entry type *">
                <select required className="input-field w-full" value={geneticEntryType}
                        onChange={(e)=>setGeneticEntryType(e.target.value)}
                        data-testid="lineage-field-entry-type">
                  <option value="">— Select —</option>
                  {GENETIC_ENTRY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>

              {/* ───── D. LINEAGE / PARENTAGE (conditional) ──────────── */}
              {showParentBlock && (
                <>
                  <SectionHeader letter="D" title="Parentage"/>
                  <ParentSection label="Parent 1" value={parentOne} onChange={setParentOne} testIdPrefix="lineage-p1"/>
                  <ParentSection label="Parent 2" value={parentTwo} onChange={setParentTwo} testIdPrefix="lineage-p2"/>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Generation">
                      <select className="input-field w-full" value={generation} onChange={(e)=>setGeneration(e.target.value)} data-testid="lineage-field-generation">
                        <option value="">—</option>
                        {GENERATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </Field>
                    <Field label="Selection direction">
                      <select className="input-field w-full" value={selectionDirection} onChange={(e)=>setSelectionDirection(e.target.value)} data-testid="lineage-field-selection-direction">
                        <option value="">—</option>
                        {SELECTION_DIRECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Number of plants selected from">
                    <input type="number" min="0" className="input-field w-full" value={plantsSelectedFrom} onChange={(e)=>setPlantsSelectedFrom(e.target.value)} data-testid="lineage-field-plants-selected"/>
                  </Field>
                </>
              )}

              {/* ───── D. LANDRACE / ORIGIN (conditional) ────────────── */}
              {showOriginBlock && (
                <>
                  <SectionHeader letter="D" title="Origin"/>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Region of origin">
                      <input className="input-field w-full" value={landraceOrigin.region}
                             onChange={(e)=>setLandraceOrigin(o => ({...o, region: e.target.value}))}
                             data-testid="lineage-field-origin-region"/>
                    </Field>
                    <Field label="Specific locality / valley / province">
                      <input className="input-field w-full" value={landraceOrigin.locality}
                             onChange={(e)=>setLandraceOrigin(o => ({...o, locality: e.target.value}))}
                             data-testid="lineage-field-origin-locality"/>
                    </Field>
                  </div>
                  <Field label="Collection location">
                    <input className="input-field w-full" value={landraceOrigin.collection_location}
                           onChange={(e)=>setLandraceOrigin(o => ({...o, collection_location: e.target.value}))}/>
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Collection year / era">
                      <input className="input-field w-full" value={landraceOrigin.collection_year}
                             onChange={(e)=>setLandraceOrigin(o => ({...o, collection_year: e.target.value}))}/>
                    </Field>
                    <Field label="Collector / preserver">
                      <input className="input-field w-full" value={landraceOrigin.collector}
                             onChange={(e)=>setLandraceOrigin(o => ({...o, collector: e.target.value}))}/>
                    </Field>
                  </div>
                  <Field label="Accession ID">
                    <input className="input-field w-full" value={landraceOrigin.accession_id}
                           onChange={(e)=>setLandraceOrigin(o => ({...o, accession_id: e.target.value}))}/>
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Population type">
                      <select className="input-field w-full" value={landraceOrigin.population_type}
                              onChange={(e)=>setLandraceOrigin(o => ({...o, population_type: e.target.value}))}>
                        <option value="">—</option>
                        {POPULATION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </Field>
                    <Field label="Preservation status">
                      <select className="input-field w-full" value={landraceOrigin.preservation_status}
                              onChange={(e)=>setLandraceOrigin(o => ({...o, preservation_status: e.target.value}))}>
                        <option value="">—</option>
                        {PRESERVATION_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Open-pollinated population size">
                    <input className="input-field w-full" value={landraceOrigin.open_pollinated_population_size}
                           onChange={(e)=>setLandraceOrigin(o => ({...o, open_pollinated_population_size: e.target.value}))}/>
                  </Field>
                  <Field label="Environmental adaptation (multi-select)">
                    <MultiSelectChips options={ENVIRONMENT_ADAPTATIONS}
                      value={landraceOrigin.environmental_adaptation || []}
                      onChange={(vals)=>setLandraceOrigin(o => ({...o, environmental_adaptation: vals}))}
                      testIdPrefix="lineage-env"/>
                  </Field>
                </>
              )}

              {/* ───── D. CLONE-ONLY DETAILS (conditional) ──────────── */}
              {showCloneBlock && (
                <>
                  <SectionHeader letter="D" title="Clone-only details"/>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Cut name">
                      <input className="input-field w-full" value={cloneDetails.cut_name}
                             onChange={(e)=>setCloneDetails(c => ({...c, cut_name: e.target.value}))}
                             data-testid="lineage-field-cut-name"/>
                    </Field>
                    <Field label="Original holder / source">
                      <input className="input-field w-full" value={cloneDetails.original_holder}
                             onChange={(e)=>setCloneDetails(c => ({...c, original_holder: e.target.value}))}/>
                    </Field>
                  </div>
                  <Field label="Suspected lineage">
                    <input className="input-field w-full" value={cloneDetails.suspected_lineage}
                           onChange={(e)=>setCloneDetails(c => ({...c, suspected_lineage: e.target.value}))}/>
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Field label="Cut authenticity">
                      <select className="input-field w-full" value={cloneDetails.cut_authenticity_status}
                              onChange={(e)=>setCloneDetails(c => ({...c, cut_authenticity_status: e.target.value}))}>
                        <option value="">—</option>
                        {CUT_AUTHENTICITY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </Field>
                    <Field label="Reversal used">
                      <select className="input-field w-full" value={cloneDetails.reversal_used}
                              onChange={(e)=>setCloneDetails(c => ({...c, reversal_used: e.target.value}))}>
                        <option value="">—</option>
                        {YES_NO_UNKNOWN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </Field>
                    <Field label="Selfed line exists">
                      <select className="input-field w-full" value={cloneDetails.selfed_line_exists}
                              onChange={(e)=>setCloneDetails(c => ({...c, selfed_line_exists: e.target.value}))}>
                        <option value="">—</option>
                        {YES_NO_UNKNOWN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Known S1 / S2 / reversal notes">
                    <textarea className="input-field w-full" rows={2} value={cloneDetails.reversal_notes}
                              onChange={(e)=>setCloneDetails(c => ({...c, reversal_notes: e.target.value}))}/>
                  </Field>
                </>
              )}

              {/* ───── E. CHEMOTYPE / TERPENES (collapsible) ─────────── */}
              <Accordion letter="E" title="Chemotype & terpenes" testid="lineage-acc-chemotype">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Chemotype">
                    <select className="input-field w-full" value={chemotype.chemotype}
                            onChange={(e)=>setChemotype(c => ({...c, chemotype: e.target.value}))}
                            data-testid="lineage-field-chemotype">
                      <option value="">—</option>
                      {CHEMOTYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Typical THC range">
                    <input className="input-field w-full" value={chemotype.thc_range}
                           onChange={(e)=>setChemotype(c => ({...c, thc_range: e.target.value}))} placeholder="18–24%"/>
                  </Field>
                  <Field label="Typical CBD range">
                    <input className="input-field w-full" value={chemotype.cbd_range}
                           onChange={(e)=>setChemotype(c => ({...c, cbd_range: e.target.value}))} placeholder="< 1%"/>
                  </Field>
                  <Field label="Dominant cannabinoids">
                    <input className="input-field w-full" value={chemotype.dominant_cannabinoids}
                           onChange={(e)=>setChemotype(c => ({...c, dominant_cannabinoids: e.target.value}))} placeholder="THC, CBG…"/>
                  </Field>
                  <Field label="Dominant terpenes">
                    <input className="input-field w-full" value={chemotype.dominant_terpenes}
                           onChange={(e)=>setChemotype(c => ({...c, dominant_terpenes: e.target.value}))} placeholder="Limonene, Caryophyllene…"/>
                  </Field>
                  <Field label="Aroma / flavor profile">
                    <input className="input-field w-full" value={chemotype.aroma_flavor}
                           onChange={(e)=>setChemotype(c => ({...c, aroma_flavor: e.target.value}))}/>
                  </Field>
                </div>
              </Accordion>

              {/* ───── F. MORPHOLOGY / GROW (collapsible) ────────────── */}
              <Accordion letter="F" title="Morphology & grow traits" testid="lineage-acc-morphology">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Leaf type">
                    <select className="input-field w-full" value={morphology.leaf_type}
                            onChange={(e)=>setMorphology(m => ({...m, leaf_type: e.target.value}))}>
                      <option value="">—</option>
                      {LEAF_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Photoperiod">
                    <select className="input-field w-full" value={morphology.photoperiod_type}
                            onChange={(e)=>setMorphology(m => ({...m, photoperiod_type: e.target.value}))}>
                      <option value="">—</option>
                      {PHOTOPERIOD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Sex expression">
                    <select className="input-field w-full" value={morphology.sex_expression}
                            onChange={(e)=>setMorphology(m => ({...m, sex_expression: e.target.value}))}>
                      <option value="">—</option>
                      {SEX_EXPRESSIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Growth structure">
                  <input className="input-field w-full" value={morphology.growth_structure}
                         onChange={(e)=>setMorphology(m => ({...m, growth_structure: e.target.value}))} placeholder="Christmas tree, bushy, lanky…"/>
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Flowering time">
                    <input className="input-field w-full" value={morphology.flowering_time}
                           onChange={(e)=>setMorphology(m => ({...m, flowering_time: e.target.value}))} placeholder="9–10 weeks"/>
                  </Field>
                  <Field label="Resin production">
                    <select className="input-field w-full" value={morphology.resin_production}
                            onChange={(e)=>setMorphology(m => ({...m, resin_production: e.target.value}))}>
                      <option value="">—</option>
                      {RESIN_PRODUCTION.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Bud structure">
                  <input className="input-field w-full" value={morphology.bud_structure}
                         onChange={(e)=>setMorphology(m => ({...m, bud_structure: e.target.value}))}/>
                </Field>
                <Field label="Yield notes">
                  <textarea className="input-field w-full" rows={2} value={morphology.yield_notes}
                            onChange={(e)=>setMorphology(m => ({...m, yield_notes: e.target.value}))}/>
                </Field>
                <Field label="Resistance notes (mold, pests, stress)">
                  <textarea className="input-field w-full" rows={2} value={morphology.resistance_notes}
                            onChange={(e)=>setMorphology(m => ({...m, resistance_notes: e.target.value}))}/>
                </Field>
              </Accordion>

              {/* ───── G. GENETIC TESTING (collapsible) ──────────────── */}
              <Accordion letter="G" title="Genetic testing (optional)" testid="lineage-acc-genetic-testing">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Test available?">
                    <select className="input-field w-full" value={geneticTesting.test_available}
                            onChange={(e)=>setGeneticTesting(g => ({...g, test_available: e.target.value}))}>
                      <option value="">—</option>
                      {[["yes","Yes"],["no","No"],["pending","Pending"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Marker type">
                    <select className="input-field w-full" value={geneticTesting.marker_type}
                            onChange={(e)=>setGeneticTesting(g => ({...g, marker_type: e.target.value}))}>
                      <option value="">—</option>
                      {GENETIC_MARKER_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Testing lab">
                    <input className="input-field w-full" value={geneticTesting.lab}
                           onChange={(e)=>setGeneticTesting(g => ({...g, lab: e.target.value}))}/>
                  </Field>
                  <Field label="Sample ID">
                    <input className="input-field w-full" value={geneticTesting.sample_id}
                           onChange={(e)=>setGeneticTesting(g => ({...g, sample_id: e.target.value}))}/>
                  </Field>
                  <Field label="Genetic cluster / population group">
                    <input className="input-field w-full" value={geneticTesting.cluster_group}
                           onChange={(e)=>setGeneticTesting(g => ({...g, cluster_group: e.target.value}))}/>
                  </Field>
                  <Field label="Estimated ancestry composition">
                    <input className="input-field w-full" value={geneticTesting.ancestry_composition}
                           onChange={(e)=>setGeneticTesting(g => ({...g, ancestry_composition: e.target.value}))} placeholder="55% Afghan, 45% Thai…"/>
                  </Field>
                </div>
                <Field label="Closest genetic matches">
                  <textarea className="input-field w-full" rows={2} value={geneticTesting.closest_matches}
                            onChange={(e)=>setGeneticTesting(g => ({...g, closest_matches: e.target.value}))}/>
                </Field>
              </Accordion>

              {/* ───── H. EVIDENCE / VERIFICATION (collapsible) ─────── */}
              <Accordion letter="H" title="Evidence & verification" testid="lineage-acc-evidence" defaultOpen>
                <Field label="Lineage confidence">
                  <select className="input-field w-full" value={verification.lineage_confidence}
                          onChange={(e)=>setVerification(v => ({...v, lineage_confidence: e.target.value}))}
                          data-testid="lineage-field-confidence">
                    <option value="">—</option>
                    {LINEAGE_CONFIDENCE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Evidence types (multi-select)">
                  <MultiSelectChips options={EVIDENCE_TYPES}
                    value={verification.evidence_types || []}
                    onChange={(vals)=>setVerification(v => ({...v, evidence_types: vals}))}
                    testIdPrefix="lineage-evidence"/>
                </Field>
                <Field label="Dispute status">
                  <select className="input-field w-full" value={verification.dispute_status}
                          onChange={(e)=>setVerification(v => ({...v, dispute_status: e.target.value}))}>
                    {DISPUTE_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>

                {/* Proof files — each item has a category + metadata */}
                <div className="mt-3 pt-3 border-t border-[var(--border)]">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-2" style={{color:"var(--text-muted)"}}>
                    Proof files
                  </div>
                  <ImageUploader
                    value={images}
                    onChange={setImages}
                    multiple
                    maxItems={10}
                    label="Upload images / scans"
                    testIdPrefix="lineage-evidence-img"
                  />
                  {images.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {images.map((url, i) => (
                        <EvidenceMetaRow key={url + i} index={i} url={url}
                          meta={evidenceFiles[i]}
                          onChange={(meta) => {
                            const next = [...evidenceFiles];
                            next[i] = { url, ...meta };
                            setEvidenceFiles(next);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <Field label="Lineage notes (where this info comes from)">
                  <textarea className="input-field w-full" rows={3} value={lineageNotes} onChange={e=>setLineageNotes(e.target.value)} maxLength={3000} data-testid="lineage-field-notes"/>
                  <ParentParseHint lineageNotes={lineageNotes}
                                    parentOne={parentOne}
                                    parentTwo={parentTwo}
                                    onApply={({ p1, p2 }) => {
                                      setParentOne((cur) => ({ ...cur, name: p1 }));
                                      setParentTwo((cur) => ({ ...cur, name: p2 }));
                                    }}/>
                </Field>
                <Field label="Source notes">
                  <textarea className="input-field w-full" rows={2} value={sourceNotes} onChange={e=>setSourceNotes(e.target.value)} maxLength={1500} data-testid="lineage-field-source"/>
                </Field>
              </Accordion>

              {/* ───── I. LEGAL / CREDIT (collapsible) ───────────────── */}
              <Accordion letter="I" title="Legal & credit" testid="lineage-acc-legal">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Submitted by (your name / handle)">
                    <input className="input-field w-full" value={legalCredit.submitted_by}
                           onChange={(e)=>setLegalCredit(l => ({...l, submitted_by: e.target.value}))}/>
                  </Field>
                  <Field label="Claimed by breeder?">
                    <select className="input-field w-full" value={legalCredit.claimed_by_breeder}
                            onChange={(e)=>setLegalCredit(l => ({...l, claimed_by_breeder: e.target.value}))}>
                      <option value="">—</option>
                      {YES_NO_UNKNOWN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Original breeder credit">
                    <input className="input-field w-full" value={legalCredit.original_breeder_credit}
                           onChange={(e)=>setLegalCredit(l => ({...l, original_breeder_credit: e.target.value}))}/>
                  </Field>
                  <Field label="Preservation credit">
                    <input className="input-field w-full" value={legalCredit.preservation_credit}
                           onChange={(e)=>setLegalCredit(l => ({...l, preservation_credit: e.target.value}))}/>
                  </Field>
                  <Field label="Commercial release date">
                    <input className="input-field w-full" value={legalCredit.commercial_release_date}
                           onChange={(e)=>setLegalCredit(l => ({...l, commercial_release_date: e.target.value}))} placeholder="2019, March 2020, etc."/>
                  </Field>
                  <Field label="IP status (trademark / PVP / patent)">
                    <input className="input-field w-full" value={legalCredit.ip_status}
                           onChange={(e)=>setLegalCredit(l => ({...l, ip_status: e.target.value}))}/>
                  </Field>
                </div>
                <Field label="License restrictions">
                  <textarea className="input-field w-full" rows={2} value={legalCredit.license_restrictions}
                            onChange={(e)=>setLegalCredit(l => ({...l, license_restrictions: e.target.value}))}/>
                </Field>
                <label className="flex items-center gap-2 text-xs mt-2" style={{color:"var(--text-2)"}}>
                  <input type="checkbox" checked={!!legalCredit.do_not_rename}
                         onChange={(e)=>setLegalCredit(l => ({...l, do_not_rename: e.target.checked}))}/>
                  Do not rename / do not claim as original work
                </label>
              </Accordion>
            </>
          )}

          {/* ───── PARENTAGE-ONLY submission (correction lane) ────────── */}
          {type === "parentage" && (
            <>
              <SectionHeader letter="D" title="Suggested parentage"/>
              <ParentSection label="Suggested Parent 1" value={parentOne} onChange={setParentOne} testIdPrefix="lineage-p1"/>
              <ParentSection label="Suggested Parent 2" value={parentTwo} onChange={setParentTwo} testIdPrefix="lineage-p2"/>
              <Field label="Lineage notes">
                <textarea className="input-field w-full" rows={3} value={lineageNotes} onChange={e=>setLineageNotes(e.target.value)} maxLength={3000} data-testid="lineage-field-notes"/>
                <ParentParseHint lineageNotes={lineageNotes}
                                  parentOne={parentOne}
                                  parentTwo={parentTwo}
                                  onApply={({ p1, p2 }) => {
                                    setParentOne((cur) => ({ ...cur, name: p1 }));
                                    setParentTwo((cur) => ({ ...cur, name: p2 }));
                                  }}/>
              </Field>
            </>
          )}

          {type === "alias" && (
            <Field label="Aliases (comma-separated)">
              <input className="input-field w-full" value={aliasInput} onChange={e=>setAliasInput(e.target.value)} data-testid="lineage-field-aliases"/>
            </Field>
          )}

          {type === "originator" && (
            <Field label="Originator (e.g. 'Bodhi Seeds', 'unknown collector')">
              <input className="input-field w-full" value={originatorText} onChange={e=>setOriginatorText(e.target.value)} maxLength={160} data-testid="lineage-field-originator"/>
            </Field>
          )}

          {type === "source_notes" && (
            <Field label="Source notes to add">
              <textarea className="input-field w-full" rows={4} value={sourceNotes} onChange={e=>setSourceNotes(e.target.value)} maxLength={1500} data-testid="lineage-field-source"/>
            </Field>
          )}

          {type === "image" && (
            <Field label="Upload image(s)">
              <ImageUploader value={images} onChange={setImages} multiple label="Up to 6 photos" maxItems={6} testIdPrefix="lineage-img-submission"/>
              <p className="text-[11px] mt-1.5" style={{color:"var(--text-muted)"}}>
                Macro shots, plant photos, finished flower, anything that helps document the strain. Admin reviews before they go public.
              </p>
            </Field>
          )}

          {type === "dispute" && (
            <div className="bg-[var(--secondary)]/10 border border-[var(--secondary)]/30 p-3 text-sm" style={{color:"var(--text-2)"}}>
              Flag this lineage as disputed. Tell us why in the note below — admin will review.
            </div>
          )}

          {/* ───── J. REVIEWER NOTE (always shown) ───────────────────── */}
          <SectionHeader letter="J" title="Reviewer note"/>
          <Field label="Reviewer note (optional)">
            <textarea className="input-field w-full" rows={2} value={note} onChange={e=>setNote(e.target.value)} maxLength={1500}
                      placeholder="Where did you learn this? Any context that helps admin verify."
                      data-testid="lineage-field-note"/>
          </Field>

          <div className="text-[11px] pt-2 border-t border-[var(--border)]" style={{color:"var(--text-muted)"}}>
            All submissions are reviewed by CannaForge before going live. Approved records show "breeder-submitted" or "community-submitted" until admin marks them verified.
          </div>
        </form>

        <div className="sticky bottom-0 flex justify-end gap-2 p-5 border-t border-[var(--border)] bg-[var(--surface)]">
          <button onClick={onClose} className="btn-outline" data-testid="lineage-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary inline-flex items-center gap-1.5" data-testid="lineage-submit-send">
            <Send className="w-4 h-4"/> {busy ? "Submitting…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Subcomponents ─────────── */

function SectionHeader({ letter, title }) {
  return (
    <div className="flex items-center gap-2 pt-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{color:"var(--gold)"}}>{letter}.</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{color:"var(--text)"}}>{title}</span>
      <span className="flex-1 h-px" style={{background:"var(--border)"}}/>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] mb-1.5"
            style={{color:"var(--text-muted)"}}>{label}</span>
      {children}
    </label>
  );
}

function Accordion({ letter, title, children, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[var(--border)]" data-testid={testid}>
      <button type="button" onClick={() => setOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[var(--surface-2)] transition-colors"
              data-testid={`${testid}-toggle`}>
        <span className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{color:"var(--gold)"}}>{letter}.</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{color:"var(--text)"}}>{title}</span>
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} style={{color:"var(--text-muted)"}}/>
      </button>
      {open && <div className="p-3 border-t border-[var(--border)] space-y-3 bg-[var(--surface-2)]/30">{children}</div>}
    </div>
  );
}

function ParentSection({ label, value, onChange, testIdPrefix }) {
  return (
    <div className="border border-[var(--border)] p-3 bg-[var(--surface-2)]/30">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-2" style={{color:"var(--gold)"}}>{label}</div>
      <Field label="Name">
        <input className="input-field w-full" value={value.name}
               onChange={(e)=>onChange({...value, name: e.target.value})}
               data-testid={`${testIdPrefix}-name`}/>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <Field label="Role">
          <select className="input-field w-full" value={value.role}
                  onChange={(e)=>onChange({...value, role: e.target.value})}
                  data-testid={`${testIdPrefix}-role`}>
            <option value="">—</option>
            {PARENT_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Type">
          <select className="input-field w-full" value={value.type}
                  onChange={(e)=>onChange({...value, type: e.target.value})}
                  data-testid={`${testIdPrefix}-type`}>
            <option value="">—</option>
            {PARENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Source / breeder">
        <input className="input-field w-full" value={value.source_breeder}
               onChange={(e)=>onChange({...value, source_breeder: e.target.value})}/>
      </Field>
    </div>
  );
}

function MultiSelectChips({ options, value, onChange, testIdPrefix }) {
  const set = new Set(value || []);
  const toggle = (v) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testIdPrefix}>
      {options.map(([v, l]) => {
        const active = set.has(v);
        return (
          <button key={v} type="button" onClick={() => toggle(v)}
                  className={`px-2 py-1 text-[11px] border font-mono uppercase tracking-[0.16em] transition-colors ${active ? "border-[var(--gold)]" : "border-[var(--border)] hover:border-[var(--gold)]"}`}
                  style={{
                    background: active ? "rgba(212,175,55,0.12)" : "transparent",
                    color: active ? "var(--gold)" : "var(--text-2)",
                  }}
                  data-testid={`${testIdPrefix}-${v}`}>
            {l}
          </button>
        );
      })}
    </div>
  );
}

function EvidenceMetaRow({ index, url, meta = {}, onChange }) {
  const update = (k, v) => onChange({ ...meta, [k]: v });
  return (
    <div className="border border-[var(--border)] p-2 bg-[var(--bg)]">
      <div className="flex items-center gap-2 mb-2">
        <img src={url} alt="" className="w-10 h-10 object-cover border border-[var(--border)] shrink-0"/>
        <div className="text-[11px] truncate" style={{color:"var(--text-muted)"}}>File #{index + 1}</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select className="input-field text-xs" value={meta.category || ""}
                onChange={(e)=>update("category", e.target.value)}>
          <option value="">— Category —</option>
          {EVIDENCE_FILE_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input className="input-field text-xs" placeholder="Date taken (YYYY-MM-DD)"
               value={meta.date_taken || ""} onChange={(e)=>update("date_taken", e.target.value)}/>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <input className="input-field text-xs" placeholder="Source / submitter"
               value={meta.source || ""} onChange={(e)=>update("source", e.target.value)}/>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.16em]" style={{color:"var(--text-muted)"}}>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={!!meta.public} onChange={(e)=>update("public", e.target.checked)}/>
            Public
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={!!meta.verification_evidence} onChange={(e)=>update("verification_evidence", e.target.checked)}/>
            Verify
          </label>
        </div>
      </div>
    </div>
  );
}

/**
 * ParentParseHint — v3.79f
 *
 * Live preview of the same lineage parser used by the admin "Parse lineage
 * text → parents" helper and the CSV importer. Renders only when:
 *   - the contributor has typed a parseable `A × B` pattern into lineage_notes
 *   - BOTH parent.name fields are currently empty (no overwrite)
 *
 * Clicking "Use detected parents" prefills parent_one.name and parent_two.name.
 * Existing manual entries are never overwritten — the hint hides itself the
 * moment either parent field has a value.
 */
function ParentParseHint({ lineageNotes, parentOne, parentTwo, onApply }) {
  const detected = useMemo(() => splitLineageText(lineageNotes || ""), [lineageNotes]);
  const hasManual = (parentOne?.name || "").trim() || (parentTwo?.name || "").trim();
  if (detected.length !== 2 || hasManual) return null;
  const [p1, p2] = detected;
  return (
    <div className="mt-2 px-3 py-2 inline-flex flex-wrap items-center gap-2 border"
         style={{
           borderColor: "var(--border)",
           background: "var(--surface-2, var(--bg-2))",
           borderRadius: 2,
         }}
         data-testid="lineage-parse-hint">
      <Wand2 className="w-3.5 h-3.5" style={{ color: "var(--gold)" }} />
      <span className="text-[11px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
        Detected parents
      </span>
      <span className="text-xs" style={{ color: "var(--text-2)" }}>
        <strong style={{ color: "var(--text)" }}>{p1}</strong>
        <span className="mx-1.5" style={{ color: "var(--gold)" }}>×</span>
        <strong style={{ color: "var(--text)" }}>{p2}</strong>
      </span>
      <button type="button"
              onClick={() => onApply?.({ p1, p2 })}
              className="ml-1 px-2 py-1 border text-[10px] font-mono uppercase tracking-[0.16em] hover:bg-[var(--bg-2)] transition-colors"
              style={{ borderColor: "var(--gold)", color: "var(--gold)", borderRadius: 2 }}
              data-testid="lineage-parse-hint-apply">
        Use detected parents
      </button>
    </div>
  );
}

